import { Button } from "@material-ui/core";
import NavButton from "../portico/button/button";
import Spacing from "../portico/spacing/spacing";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from 'react-redux';
import { updateEntityDraft } from '../../services/fenergo-service';
import { useSelectedEntity } from "../../stores/entity/entity.hooks";
import { decreaseCurrentStepperIndex, increaseCurrentStepperIndex, setDisableButtonCheck, setSnackBar } from '../../stores/ui/ui-action';
import { useCodatState, useCurrentStepperIndex, useDisableButton  } from "../../stores/ui/ui.hooks";
import CONSTANTS from "../../utils/constants";
import { FormData } from "../custom/forms/form-data";
import { getFormDataValuesObjectForDraftApi, getFormInitialValuesFromEntityResponse } from "../custom/forms/form-helper-methods";
import DataPolicyTaskDetailsStyles from "./data-policy-task-details.styles";
import CustomLoading from "../custom/custom-loading";
import { checkOtherErrors, filterSubField , getDataGroupBlankFieldCount, isOptionalField } from "../../utils/common-utils";
import { getLoadMessage } from "../../utils/journey-task-utils";

interface DataPolicyTaskDetailsProps {
  readOnly: boolean;
  journeyId: string;
  entityDraft: any;
  currentTask: any;
  completeTask: Function;
  buttonLabel: string;
  handleBackClick: Function;
  journeyName?: string;
}

export const DataPolicyTaskDetails: React.FC<DataPolicyTaskDetailsProps> = ({
  readOnly, journeyId, journeyName, entityDraft, currentTask, completeTask, buttonLabel, handleBackClick
}) => {
    const disableButton = useDisableButton();
    const codatState = useCodatState();
  const dynamicRequirementsLoadInProgress = useSelector((state: any) => state.entity?.dynamicRequirementsLoadInProgress);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [showButton, setshowButton] = useState<boolean>(false);

  const dispatch = useDispatch();
  const selectedEntity = useSelectedEntity();
  const activeStepperIndex = useCurrentStepperIndex();
  const formMethods = useForm({ shouldUnregister: true, defaultValues: getFormInitialValuesFromEntityResponse(entityDraft), mode: 'onBlur'});
  const classes = DataPolicyTaskDetailsStyles();
  
  const checkLoadInProgress = () => {
    if(currentTask.name === "Fundraising") {
      return dynamicRequirementsLoadInProgress === false;
    }
    return true;
  }

  useEffect(() => {
    if (Object.keys(formMethods.formState.errors.valueOf()).length === 0 && checkLoadInProgress()) {
      dispatch(setDisableButtonCheck(checkAllFields()));
    } 
    else {
        dispatch(setDisableButtonCheck(true));
    }
  }) 

  const updateEntityDraftForTask = async (entityId, entityDraftId, formData) => {
    try {
      await updateEntityDraft(entityId, entityDraftId, formData);
      return true;
    } catch (error) {
      if(error?.response?.status !== 400){
        dispatch(setSnackBar({ show: true, message: 'Error while calling API', description: error.message, messageType: 'error' }));
      }
      setSubmitting(false);
      return false;
    }
  }

  const handleFormSubmitted = async (values) => {
    setSubmitting(true);

    const formData = getFormDataValuesObjectForDraftApi(CONSTANTS.CLIENT, values, entityDraft.jurisdictions, CONSTANTS.COMPANY, currentTask["businessCategory"]);
    const isSuccess = await updateEntityDraftForTask(selectedEntity.id, entityDraft.id, { data: formData });

    if (isSuccess) {
      await completeTask();
      dispatch(increaseCurrentStepperIndex());
    }
    
    setSubmitting(false);
  };

  const onClickDisabledButton = async () => {
    if (disableButton) {
      const result = await formMethods.trigger();
      const errorElem = document.querySelector(`[name=${Object.keys(formMethods.formState.errors)[0]}]`)
      if(errorElem) errorElem.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  }

  const checkAllFields = () => {
    const formValues = Object.entries(formMethods.getValues())
      .filter(entry => !isOptionalField(entry[0]));

    // If the Capped radio button (parent radio button in this example) is selected and then unselected,
    // the whatIsTheValuationCap field is not properly unregistered because the FormInput component does
    // not unregister itself when the component is unmounted. Once that issue is fixed, the line below
    // and the extra filter below should be able to be removed.
    const ignorableSubFields = {
      "whenDoYouPlanToRaiseAnotherRound": {parentKey: "doYouPlanOnRaisingAnotherRoundOfFunding", parentValue:"No"},
      "equityValuationAtLastRound": {parentKey:"whatTypeOfFundsDidYouRaiseInYourLastRound", parentValue:"SAFE"},
      "whatIsTheValuationCap": {parentKey:"isYourSafeAgreementCappedOrUncapped", parentValue:"Uncapped"},
      "codatCompanyId": {parentKey:"uploadAccountingDocumentsManually", parentValue:"Yes"}
    };

    const parentValues = Object.values(ignorableSubFields).map(entry => {
        return {[entry.parentKey]: formValues.find(formValue => formValue[0] === entry.parentKey)?.[1] ?? ''};
    }).reduce((previous, current) => {return {...previous, ...current}},{});

    const blankFields = formValues.filter(entry => !entry[1])
    // this filter below should be able to be removed once the above issue is fixed
      .filter(entry => filterSubField(entry[0], ignorableSubFields, parentValues));

    const blankFieldsGroupData=getDataGroupBlankFieldCount(formValues, CONSTANTS.IGNORABLE_FIELDS)

    const otherErrorsCount = checkOtherErrors(currentTask.name, formValues);

    // If there are blank fields remaining, we want to disable the button.
    return blankFields.length > 0 || blankFieldsGroupData > 0 || otherErrorsCount > 0;
  }

  const onBackClick = () => {
    dispatch(decreaseCurrentStepperIndex());
    handleBackClick();
  }

  const getDisableState = (currentTask) => {
    const element = document.querySelector('[data-testid="AddCircleOutlineIcon"]');
    if(currentTask.name == "Financials" && element) {
      return codatState.companyId == null || codatState.platformName == null;
    }
  }

  return (
    <>
      <form data-testid="data-policy-form" noValidate
        onSubmit={formMethods.handleSubmit(handleFormSubmitted)}
      >
        {submitting && <CustomLoading message={ getLoadMessage(currentTask.name) } />}
        {currentTask && entityDraft && (
          <FormData
            key={currentTask["id"]}
            initialValues={getFormInitialValuesFromEntityResponse(entityDraft)}
            journeyId={journeyId}
            journeyName={journeyName}
            taskId={currentTask["id"]}
            entityDraft={entityDraft}
            entityType={entityDraft["type"]}
            policyType={currentTask["policyRequirementType"]}
            categories={currentTask["businessCategory"]}
            targetEntity={currentTask["policyTarget"]}
            readOnly={readOnly}
            enableButton={setshowButton}
            currentTaskName={currentTask.name}
            formMethods={formMethods}
          />
        )}
        <Spacing variable="spacing-base-x3" />
        {showButton && entityDraft && currentTask && (
          <>
            <div className={classes.buttoncontainer}>
              {activeStepperIndex !== 0 && <div className={classes.button}><Button onClick={onBackClick} className={classes.backButton} variant="outlined">Back</Button></div>}
              <div className={classes.button} onClick={onClickDisabledButton}>
                <NavButton 
                  disableButton={disableButton || getDisableState(currentTask)}
                  dataCy='save-btn'
                  buttonType="submit"
                  buttonLabel={buttonLabel} />
              </div>
            </div>
          </>
        )}
      </form> 
    </>
  );
};